import { Product, LineItemProduct } from "../types/products";

/**
 * Filters products based on search query
 * @param products - Array of products to filter
 * @param query - Search query string
 * @returns Filtered array of products
 */
export const filterProducts = (
  products: Product[],
  query: string,
): Product[] => {
  if (!query.trim()) return products;

  const lowerQuery = query.toLowerCase();

  return products.filter(
    (product) =>
      product.custom_id.toLowerCase().includes(lowerQuery) ||
      product.name.toLowerCase().includes(lowerQuery) ||
      product.company.toLowerCase().includes(lowerQuery) ||
      product.category.toLowerCase().includes(lowerQuery),
  );
};

/**
 * Sorts products by relevance to search query
 * @param products - Array of products to sort
 * @param query - Search query string
 * @returns Sorted array of products
 */
export const sortProductsByRelevance = (
  products: Product[],
  query: string,
): Product[] => {
  if (!query.trim()) return products;

  const lowerQuery = query.toLowerCase();

  return products.sort((a, b) => {
    let scoreA = 0;
    let scoreB = 0;

    // custom_id gets highest weight
    if (a.custom_id.toLowerCase().includes(lowerQuery)) scoreA += 100;
    if (b.custom_id.toLowerCase().includes(lowerQuery)) scoreB += 100;

    // name gets high weight
    if (a.name.toLowerCase().includes(lowerQuery)) scoreA += 50;
    if (b.name.toLowerCase().includes(lowerQuery)) scoreB += 50;

    // company gets medium weight
    if (a.company.toLowerCase().includes(lowerQuery)) scoreA += 25;
    if (b.company.toLowerCase().includes(lowerQuery)) scoreB += 25;

    // category gets lowest weight
    if (a.category.toLowerCase().includes(lowerQuery)) scoreA += 10;
    if (b.category.toLowerCase().includes(lowerQuery)) scoreB += 10;

    return scoreB - scoreA;
  });
};

/**
 * Paginates products array
 * @param products - Array of products to paginate
 * @param page - Current page number (1-based)
 * @param limit - Number of items per page
 * @returns Object with paginated products and pagination info
 */
export const paginateProducts = (
  products: Product[],
  page: number,
  limit: number,
): {
  products: Product[];
  total: number;
  hasMore: boolean;
  currentPage: number;
} => {
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedProducts = products.slice(startIndex, endIndex);

  return {
    products: paginatedProducts,
    total: products.length,
    hasMore: endIndex < products.length,
    currentPage: page,
  };
};

/**
 * Searches and paginates products with relevance sorting
 * @param products - Array of all products
 * @param query - Search query
 * @param page - Current page number
 * @param limit - Items per page
 * @returns Paginated and sorted results
 */
export const searchAndPaginateProducts = (
  products: Product[],
  query: string,
  page: number = 1,
  limit: number = 10,
  statusFilter?: string,
): {
  products: Product[];
  total: number;
  hasMore: boolean;
  currentPage: number;
} => {
  let filteredProducts = products;

  // Apply status filter first
  if (statusFilter && statusFilter !== "All") {
    filteredProducts = filteredProducts.filter(
      (product) => product.status === statusFilter,
    );
  }

  // Then apply search query
  filteredProducts = filterProducts(filteredProducts, query);
  const sortedProducts = sortProductsByRelevance(filteredProducts, query);

  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedProducts = sortedProducts.slice(startIndex, endIndex);

  return {
    products: paginatedProducts,
    total: filteredProducts.length,
    hasMore: endIndex < filteredProducts.length,
    currentPage: page,
  };
};

// Function for searching LineItemProducts (for transaction compatibility)
export const searchAndPaginateLineItemProducts = (
  products: LineItemProduct[],
  query: string,
  page: number = 1,
  limit: number = 10,
): {
  products: LineItemProduct[];
  total: number;
  hasMore: boolean;
  currentPage: number;
} => {
  const filteredProducts = filterLineItemProducts(products, query);
  const sortedProducts = sortLineItemProductsByRelevance(
    filteredProducts,
    query,
  );

  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedProducts = sortedProducts.slice(startIndex, endIndex);

  return {
    products: paginatedProducts,
    total: filteredProducts.length,
    hasMore: endIndex < filteredProducts.length,
    currentPage: page,
  };
};

/**
 * Gets unique categories from products
 * @param products - Array of products
 * @returns Array of unique category names
 */
export const getUniqueCategories = (products: Product[]): string[] => {
  const categories = new Set<string>();
  products.forEach((product) => {
    if (product.category) {
      categories.add(product.category);
    }
  });
  return Array.from(categories).sort();
};

/**
 * Gets unique companies from products
 * @param products - Array of products
 * @returns Array of unique company names
 */
export const getUniqueCompanies = (products: Product[]): string[] => {
  const companies = new Set<string>();
  products.forEach((product) => {
    if (product.company) {
      companies.add(product.company);
    }
  });
  return Array.from(companies).sort();
};

/**
 * Groups products by category
 * @param products - Array of products
 * @returns Object with categories as keys and product arrays as values
 */
export const groupProductsByCategory = (
  products: Product[],
): Record<string, Product[]> => {
  return products.reduce(
    (acc, product) => {
      const category = product.category || "Uncategorized";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(product);
      return acc;
    },
    {} as Record<string, Product[]>,
  );
};

/**
 * Checks if a product matches all filter criteria
 * @param product - Product to check
 * @param filters - Filter criteria
 * @returns Boolean indicating if product matches filters
 */
export const productMatchesFilters = (
  product: Product,
  filters: {
    category?: string;
    company?: string;
    minPrice?: number;
    maxPrice?: number;
    inStock?: boolean;
  },
): boolean => {
  if (filters.category && product.category !== filters.category) return false;
  if (filters.company && product.company !== filters.company) return false;
  if (filters.minPrice !== undefined && product.list_price < filters.minPrice)
    return false;
  if (filters.maxPrice !== undefined && product.list_price > filters.maxPrice)
    return false;
  if (filters.inStock && product.quantity <= 0) return false;

  return true;
};

// New function to map Product to LineItemProduct for transaction compatibility
export const mapProductToLineItem = (product: Product): LineItemProduct => {
  return {
    id: product.id,
    line_item_id: "", // Will be set when added to transaction
    quantity: 1, // Default quantity
    unit_price: product.list_price,
    source: "inventory",
    custom_id: product.custom_id,
    name: product.name,
    category: product.category,
    company: product.company,
    purchase_price: product.purchase_price,
    list_price: product.list_price,
    description: product.description,
    img_url: product.img_url,
    available_stock: product.quantity,
    reorder_threshold: product.reorder_threshold,
    status: product.status,
    notes: product.notes,
    transaction_id: "", // Will be set when added to transaction
  };
};

// Maintain backward compatibility for existing transaction components
export const filterLineItemProducts = (
  products: LineItemProduct[],
  query: string,
): LineItemProduct[] => {
  if (!query.trim()) return products;

  const lowerQuery = query.toLowerCase();

  return products.filter(
    (product) =>
      product.custom_id.toLowerCase().includes(lowerQuery) ||
      product.name.toLowerCase().includes(lowerQuery) ||
      (product.company && product.company.toLowerCase().includes(lowerQuery)) ||
      product.category.toLowerCase().includes(lowerQuery),
  );
};

export const sortLineItemProductsByRelevance = (
  products: LineItemProduct[],
  query: string,
): LineItemProduct[] => {
  if (!query.trim()) return products;

  const lowerQuery = query.toLowerCase();

  return products.sort((a, b) => {
    let scoreA = 0;
    let scoreB = 0;

    // custom_id gets highest weight
    if (a.custom_id.toLowerCase().includes(lowerQuery)) scoreA += 100;
    if (b.custom_id.toLowerCase().includes(lowerQuery)) scoreB += 100;

    // name gets high weight
    if (a.name.toLowerCase().includes(lowerQuery)) scoreA += 50;
    if (b.name.toLowerCase().includes(lowerQuery)) scoreB += 50;

    // company gets medium weight
    if (a.company && a.company.toLowerCase().includes(lowerQuery)) scoreA += 25;
    if (b.company && b.company.toLowerCase().includes(lowerQuery)) scoreB += 25;

    // category gets lowest weight
    if (a.category.toLowerCase().includes(lowerQuery)) scoreA += 10;
    if (b.category.toLowerCase().includes(lowerQuery)) scoreB += 10;

    return scoreB - scoreA;
  });
};
